import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { LicenseModel } from '../../../../../typings/api/skymap/rest/v0/.common';
import { UserByRequestResponse } from '../../../../../typings/api/skymap/rest/v0/user';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../utils/variables';
import { useOptionalAuthState } from '../auth-state';
import type { Project } from '../project-state/project-state';
import { projectStateAdapter } from '../project-state/project-state-adapter';
import {
  hasDesignToolAccessForCurrentProject as computeHasDesignToolAccess,
  hasValidDesignToolSeat as computeHasValidDesignToolSeat,
} from './design-access';
import { getNavigationItemsFromUser, type NavigationItem } from './navigation-from-user';

export type User = UserByRequestResponse['data'];

const LICENSES_QUERY_KEY = ['user', 'licenses'] as const;

export interface UserStateValue {
  user: User | undefined;
  licenses: LicenseModel[];
  isLoaded: boolean;
  isLoadingLicenses: boolean;
  isOrganizationAdmin: boolean;
  isCompanyAdmin: boolean;
  getName: () => string;
  getActiveUserId: () => string | undefined;
  getProjects: () => User['projects'] | undefined;
  getCompanyUsers: () => User['companyusers'] | undefined;
  hasValidDesignToolSeat: () => boolean;
  hasDesignToolAccessForCurrentProject: () => boolean;
  navigationItems: NavigationItem[];
  getActiveRole: () => NavigationItem | null;
  setActiveRole: (role: NavigationItem | null) => void;
  isActiveRoleProjectUser: () => boolean;
  isActiveRoleCompanyAdmin: () => boolean;
  isActiveRoleOrganizationAdmin: () => boolean;
}

const defaultUserState: UserStateValue = {
  user: undefined,
  licenses: [],
  isLoaded: false,
  isLoadingLicenses: false,
  isOrganizationAdmin: false,
  isCompanyAdmin: false,
  getName: () => '',
  getActiveUserId: () => undefined,
  getProjects: () => undefined,
  getCompanyUsers: () => undefined,
  hasValidDesignToolSeat: () => false,
  hasDesignToolAccessForCurrentProject: () => false,
  navigationItems: [],
  getActiveRole: () => null,
  setActiveRole: () => undefined,
  isActiveRoleProjectUser: () => false,
  isActiveRoleCompanyAdmin: () => false,
  isActiveRoleOrganizationAdmin: () => false,
};

/** Exported for tests that render with a custom UserStateContext.Provider (e.g. user-state-context.tsx). */
export const UserStateContext = React.createContext<UserStateValue>(defaultUserState);

const STORAGE_KEY_SELECTED_ROLE = 'selectedRole';

function readStoredRole(): NavigationItem | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SELECTED_ROLE);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as NavigationItem;
  } catch {
    return null;
  }
}

function writeStoredRole(role: NavigationItem | null) {
  if (role) {
    localStorage.setItem(STORAGE_KEY_SELECTED_ROLE, JSON.stringify(role));
  } else {
    localStorage.removeItem(STORAGE_KEY_SELECTED_ROLE);
  }
}

type Props = {
  children: React.ReactNode;
};

export function UserStateProvider(props: Props) {
  const auth = useOptionalAuthState();
  const user = auth?.status === 'authenticated' ? auth.user : undefined;

  const [projectSnapshot, setProjectSnapshot] = useState(() => projectStateAdapter.getSnapshot());

  useEffect(() => {
    return projectStateAdapter.subscribe(() => {
      setProjectSnapshot(projectStateAdapter.getSnapshot());
    });
  }, []);

  const { data: licenses = [], isLoading: isLoadingLicenses } = useQuery({
    queryKey: [...LICENSES_QUERY_KEY, user?.id],
    queryFn: async () => {
      const res = await SkyMapAxiosServiceFactory.instance
        .createUserServiceV0()
        .getLicensesForCurrentUser({
          query: {
            include: ['license.licenseDemoPeriods', 'license.company', 'license.company.country'],
          },
        });
      return res.data;
    },
    enabled: isDefined(user?.id),
  });

  const project = projectSnapshot.project ?? null;
  const projectWithLicenses = project as Project & { companyUserLicenses?: LicenseModel[] };

  const [activeRole, setActiveRoleState] = useState<NavigationItem | null>(() => readStoredRole());

  const hasValidDesignToolSeat = useMemo(
    () => () => computeHasValidDesignToolSeat(user, project),
    [user, project],
  );

  const hasDesignToolAccessForCurrentProject = useMemo(
    () => () => computeHasDesignToolAccess(user, licenses, projectWithLicenses),
    [user, licenses, projectWithLicenses],
  );

  const navigationItems = useMemo(() => getNavigationItemsFromUser(user), [user]);

  const getActiveRole = useCallback((): NavigationItem | null => {
    if (activeRole && navigationItems.some((item) => item.id === activeRole.id)) {
      return activeRole;
    }
    return navigationItems.length > 0 ? navigationItems[0] : null;
  }, [activeRole, navigationItems]);

  const setActiveRole = useCallback((role: NavigationItem | null) => {
    writeStoredRole(role);
    setActiveRoleState(role);
  }, []);

  const findRoleKeyByActiveRoleId = useCallback((): string | undefined => {
    const active = getActiveRole();
    if (!active) {
      return undefined;
    }
    if (active.id === 'dashboard') {
      return 'user';
    }
    if (active.id === 'companyAdmin') {
      return 'companyAdmin';
    }
    if (active.id === 'organizationAdmin') {
      return 'organizationAdmin';
    }
    return undefined;
  }, [getActiveRole]);

  const isActiveRoleProjectUser = useCallback(
    () => findRoleKeyByActiveRoleId() === 'user',
    [findRoleKeyByActiveRoleId],
  );
  const isActiveRoleCompanyAdmin = useCallback(
    () => findRoleKeyByActiveRoleId() === 'companyAdmin',
    [findRoleKeyByActiveRoleId],
  );
  const isActiveRoleOrganizationAdmin = useCallback(
    () => findRoleKeyByActiveRoleId() === 'organizationAdmin',
    [findRoleKeyByActiveRoleId],
  );

  const value = useMemo<UserStateValue>(
    () => ({
      user,
      licenses,
      isLoaded: isDefined(user),
      isLoadingLicenses,
      isOrganizationAdmin: user?.isOrganizationAdmin ?? false,
      isCompanyAdmin: (user?.companyusers ?? []).length > 0,
      getName: () => (user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : ''),
      getActiveUserId: () => user?.id,
      getProjects: () => user?.projects,
      getCompanyUsers: () => user?.companyusers,
      hasValidDesignToolSeat,
      hasDesignToolAccessForCurrentProject,
      navigationItems,
      getActiveRole,
      setActiveRole,
      isActiveRoleProjectUser,
      isActiveRoleCompanyAdmin,
      isActiveRoleOrganizationAdmin,
    }),
    [
      user,
      licenses,
      isLoadingLicenses,
      hasValidDesignToolSeat,
      hasDesignToolAccessForCurrentProject,
      navigationItems,
      getActiveRole,
      setActiveRole,
      isActiveRoleProjectUser,
      isActiveRoleCompanyAdmin,
      isActiveRoleOrganizationAdmin,
    ],
  );

  return <UserStateContext.Provider value={value}>{props.children}</UserStateContext.Provider>;
}

export function useUserState(): UserStateValue {
  const ctx = React.useContext(UserStateContext);
  if (!ctx) {
    throw new Error('useUserState must be used within a UserStateProvider');
  }
  return ctx;
}

export function useOptionalUserState(): UserStateValue | undefined {
  return React.useContext(UserStateContext);
}
